// src/ReportGenerator.js

export const generatePDFReport = (comprehensiveReport, formData) => {
  // Generate insurance considerations
  const insuranceConsiderations = generateInsuranceForReport(formData);

  // Generate HTML content for the report
  const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>EHS & Insurance Readiness Assessment Report</title>
  <style>
    body {
      font-family: Arial, sans-serif;
      line-height: 1.6;
      color: #333;
      max-width: 1200px;
      margin: 0 auto;
      padding: 20px;
      background: #f8fafc;
    }
    .container {
      background: white;
      padding: 40px;
      box-shadow: 0 4px 6px rgba(0,0,0,0.1);
    }
    .header {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      padding: 40px;
      margin: -40px -40px 30px -40px;
      border-radius: 8px 8px 0 0;
    }
    .header h1 {
      margin: 0 0 10px 0;
      font-size: 32px;
    }
    .header p {
      margin: 0;
      opacity: 0.9;
    }
    .print-button {
      position: fixed;
      top: 20px;
      right: 20px;
      background: #3b82f6;
      color: white;
      border: none;
      padding: 12px 24px;
      border-radius: 6px;
      cursor: pointer;
      font-size: 14px;
      font-weight: bold;
      box-shadow: 0 4px 6px rgba(0,0,0,0.1);
    }
    .print-button:hover {
      background: #2563eb;
    }
    .section {
      margin-bottom: 40px;
      page-break-inside: avoid;
    }
    .section-title {
      font-size: 24px;
      color: #1e40af;
      border-bottom: 3px solid #3b82f6;
      padding-bottom: 10px;
      margin-bottom: 20px;
    }
    .subsection {
      background: #f8fafc;
      padding: 20px;
      margin-bottom: 20px;
      border-left: 4px solid #3b82f6;
      border-radius: 4px;
    }
    .subsection-title {
      font-size: 18px;
      font-weight: bold;
      color: #1e293b;
      margin-bottom: 10px;
    }
    .risk-high {
      background: #fee2e2;
      border-left-color: #dc2626;
    }
    .risk-medium {
      background: #fef3c7;
      border-left-color: #f59e0b;
    }
    .badge {
      display: inline-block;
      padding: 4px 12px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: bold;
      margin-left: 10px;
    }
    .badge-high {
      background: #dc2626;
      color: white;
    }
    .badge-medium {
      background: #f59e0b;
      color: white;
    }
    .cost-estimate {
      background: #dbeafe;
      padding: 10px;
      border-radius: 4px;
      margin-top: 10px;
      font-weight: bold;
    }
    .action-list {
      margin-left: 20px;
    }
    .action-list li {
      margin-bottom: 8px;
    }
    .citation {
      font-size: 12px;
      color: #6b7280;
      margin-top: 5px;
    }
    .link {
      color: #2563eb;
      text-decoration: none;
    }
    .link:hover {
      text-decoration: underline;
    }
    .disclaimer {
      background: #fef3c7;
      border: 2px solid #f59e0b;
      padding: 20px;
      margin: 30px 0;
      border-radius: 8px;
    }
    .footer {
      text-align: center;
      color: #6b7280;
      font-size: 12px;
      margin-top: 50px;
      padding-top: 20px;
      border-top: 1px solid #e5e7eb;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin: 20px 0;
    }
    th, td {
      padding: 12px;
      text-align: left;
      border-bottom: 1px solid #e5e7eb;
    }
    th {
      background: #f1f5f9;
      font-weight: bold;
    }
    @media print {
      .print-button {
        display: none;
      }
      .section {
        page-break-inside: avoid;
      }
      body {
        background: white;
      }
      .container {
        box-shadow: none;
        padding: 0;
      }
      .header {
        margin: 0 0 30px 0;
      }
    }
  </style>
</head>
<body>
  <button class="print-button" onclick="window.print()">Print / Save as PDF</button>
  
  <div class="container">
    <div class="header">
      <h1>EHS & Insurance Readiness Assessment</h1>
      <p>Comprehensive Stage 1 Risk Analysis Report</p>
      <p>Generated: ${new Date().toLocaleDateString()}</p>
    </div>

    <div class="disclaimer">
      <strong>Important Disclaimer:</strong> This report provides educational information about common compliance patterns and insurance market trends based on the operational profile provided. It does not constitute legal advice, insurance advice, or a guarantee of coverage or compliance. Organizations should consult with qualified legal, EHS, and insurance professionals for specific guidance tailored to their unique circumstances.
    </div>

    <!-- Executive Summary -->
    <div class="section">
      <h2 class="section-title">Executive Summary</h2>
      
      <div class="subsection">
        <div class="subsection-title">Company Profile</div>
        <table>
          <tr>
            <th>Industry</th>
            <td>${comprehensiveReport.executiveSummary.companyProfile.industry}</td>
          </tr>
          <tr>
            <th>Employees</th>
            <td>${comprehensiveReport.executiveSummary.companyProfile.employees}</td>
          </tr>
          <tr>
            <th>Revenue</th>
            <td>${comprehensiveReport.executiveSummary.companyProfile.revenue}</td>
          </tr>
          <tr>
            <th>Facilities</th>
            <td>${comprehensiveReport.executiveSummary.companyProfile.facilities}</td>
          </tr>
          <tr>
            <th>Operating States</th>
            <td>${comprehensiveReport.executiveSummary.companyProfile.states.join(', ')}</td>
          </tr>
        </table>
      </div>

      <div class="subsection">
        <div class="subsection-title">Priority Actions</div>
        <table>
          <thead>
            <tr>
              <th>Action</th>
              <th>Timeline</th>
              <th>Estimated Cost</th>
            </tr>
          </thead>
          <tbody>
            ${comprehensiveReport.executiveSummary.priorityActions.map(action => `
              <tr>
                <td>${action.action}</td>
                <td>${action.timeline}</td>
                <td>${action.cost}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    </div>

    <!-- Facility Analysis -->
    <div class="section">
      <h2 class="section-title">Facility-by-Facility Analysis</h2>
      ${comprehensiveReport.facilityAnalysis.map((facility, idx) => `
        <div class="subsection">
          <div class="subsection-title">Facility ${idx + 1}</div>
          
          ${facility.regulatoryProfile.map(reg => `
            <div style="margin-bottom: 25px;">
              <h3 style="color: #1e40af; margin-bottom: 10px;">${reg.regulation}</h3>
              <p><strong>Trigger:</strong> ${reg.trigger}</p>
              <p><strong>Requirement:</strong> ${reg.requirement}</p>
              
              <p style="margin-top: 15px;"><strong>Required Actions:</strong></p>
              <ul class="action-list">
                ${reg.actionItems.map(item => `<li>${item}</li>`).join('')}
              </ul>
              
              ${reg.costs ? `
                <div class="cost-estimate">
                  <strong>Estimated Costs:</strong><br>
                  ${Object.entries(reg.costs).map(([key, value]) => 
                    `${key.replace(/([A-Z])/g, ' $1').trim()}: ${value}`
                  ).join('<br>')}
                </div>
              ` : ''}
              
              ${reg.links ? `
                <p style="margin-top: 10px;">
                  ${reg.links.map(link => 
                    `<a href="${link.url}" class="link" target="_blank">${link.text}</a>`
                  ).join(' | ')}
                </p>
              ` : ''}
            </div>
          `).join('')}
          
          ${facility.commonInspectionFindings.length > 0 ? `
            <div style="margin-top: 20px;">
              <h4>Common Inspection Findings:</h4>
              <ul class="action-list">
                ${facility.commonInspectionFindings.map(finding => `<li>${finding}</li>`).join('')}
              </ul>
            </div>
          ` : ''}
        </div>
      `).join('')}
    </div>

    <!-- OSHA Requirements -->
    <div class="section">
      <h2 class="section-title">OSHA Compliance Requirements</h2>
      ${comprehensiveReport.regulatoryCompliance.oshaRequirements.map(req => `
        <div class="subsection ${req.standard.includes('Process Safety') ? 'risk-high' : ''}">
          <div class="subsection-title">${req.standard}</div>
          <p><strong>Requirement:</strong> ${req.requirement}</p>
          
          ${req.chemicalsPresent ? `
            <p style="background: #fee2e2; padding: 10px; border-radius: 4px; margin: 10px 0;">
              <strong>PSM-Covered Chemicals Present:</strong> ${req.chemicalsPresent}
            </p>
          ` : ''}
          
          <p style="margin-top: 15px;"><strong>Action Items:</strong></p>
          <ul class="action-list">
            ${req.actionItems.map(item => `<li>${item}</li>`).join('')}
          </ul>
          
          ${req.training ? `<p><strong>Training:</strong> ${req.training}</p>` : ''}
          
          ${req.citations ? `
            <p style="background: #fef3c7; padding: 10px; border-radius: 4px; margin: 10px 0;">
              <strong>⚠️ Enforcement Note:</strong> ${req.citations}
            </p>
          ` : ''}
          
          ${req.costs ? `
            <div class="cost-estimate">
              <strong>Estimated Costs:</strong><br>
              ${Object.entries(req.costs).map(([key, value]) => 
                `${key.replace(/([A-Z])/g, ' $1').trim()}: ${value}`
              ).join('<br>')}
            </div>
          ` : ''}
        </div>
      `).join('')}
    </div>

    <!-- State Requirements -->
    ${comprehensiveReport.regulatoryCompliance.stateRequirements.length > 0 ? `
      <div class="section">
        <h2 class="section-title">State-Specific Requirements</h2>
        ${comprehensiveReport.regulatoryCompliance.stateRequirements.map(state => `
          <div class="subsection">
            <div class="subsection-title">${state.state}</div>
            <p><strong>Environmental Agency:</strong> 
              <a href="${state.environmentalAgency.url}" class="link" target="_blank">
                ${state.environmentalAgency.name}
              </a>
            </p>
            <p><strong>OSHA Program:</strong> ${state.oshaProgram}</p>
            <p>${state.oshaNote}</p>
          </div>
        `).join('')}
        
        <div class="subsection">
          <div class="subsection-title">Additional Resources</div>
          <p>
            <a href="https://www.osha.gov/stateplans" class="link" target="_blank">OSHA State Plans Overview</a> | 
            <a href="https://www.osha.gov/laws-regs" class="link" target="_blank">OSHA Laws & Regulations</a> | 
            <a href="https://www.osha.gov/sites/default/files/publications/osha2254.pdf" class="link" target="_blank">OSHA Training Requirements (OSHA 2254)</a>
          </p>
        </div>
      </div>
    ` : ''}

    <!-- Insurance Coverage Analysis -->
    <div class="section">
      <h2 class="section-title">Insurance Coverage Analysis</h2>
      <p style="margin-bottom: 20px;">Detailed insurance considerations based on your operational profile:</p>

      <div class="subsection" style="background: #fef3c7; border-left-color: #f59e0b;">
        <p style="font-size: 14px;"><strong>Current Insurance Posture:</strong> ${formData.insuranceType}</p>
        <p style="font-size: 14px;"><strong>Pollution Coverage:</strong> ${formData.hasPollutionCoverage ? 'Yes' : 'No'}</p>
      </div>

      ${insuranceConsiderations.map(item => `
        <div class="subsection">
          <div class="subsection-title">${item.area}</div>
          <p><strong>Coverage Type:</strong> <span class="badge badge-medium">${item.coverage}</span></p>
          <p style="margin-top: 10px;">${item.insight}</p>
        </div>
      `).join('')}
    </div>

    <!-- Implementation Roadmap -->
    <div class="section">
      <h2 class="section-title">Implementation Roadmap</h2>
      
      <div class="subsection">
        <div class="subsection-title">Immediate Actions (0-30 Days)</div>
        <table>
          <thead>
            <tr>
              <th>Action</th>
              <th>Cost</th>
              <th>Priority</th>
            </tr>
          </thead>
          <tbody>
            ${comprehensiveReport.implementationRoadmap.immediate.map(item => `
              <tr>
                <td>${item.action}</td>
                <td>${item.cost}</td>
                <td><span class="badge badge-high">${item.priority}</span></td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>

      <div class="subsection">
        <div class="subsection-title">Short-Term Actions (30-90 Days)</div>
        <table>
          <thead>
            <tr>
              <th>Action</th>
              <th>Cost</th>
              <th>Priority</th>
            </tr>
          </thead>
          <tbody>
            ${comprehensiveReport.implementationRoadmap.shortTerm.map(item => `
              <tr>
                <td>${item.action}</td>
                <td>${item.cost}</td>
                <td><span class="badge badge-medium">${item.priority}</span></td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>

      <div class="subsection">
        <div class="subsection-title">Medium-Term Actions (90-180 Days)</div>
        <table>
          <thead>
            <tr>
              <th>Action</th>
              <th>Cost</th>
              <th>Priority</th>
            </tr>
          </thead>
          <tbody>
            ${comprehensiveReport.implementationRoadmap.mediumTerm.map(item => `
              <tr>
                <td>${item.action}</td>
                <td>${item.cost}</td>
                <td>${item.priority}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>

      <div class="subsection">
        <div class="subsection-title">Long-Term Actions (180+ Days)</div>
        <table>
          <thead>
            <tr>
              <th>Action</th>
              <th>Cost</th>
              <th>Priority</th>
            </tr>
          </thead>
          <tbody>
            ${comprehensiveReport.implementationRoadmap.longTerm.map(item => `
              <tr>
                <td>${item.action}</td>
                <td>${item.cost}</td>
                <td>${item.priority}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
    </div>

    <!-- Citation Index -->
    <div class="section">
      <h2 class="section-title">Regulatory Citation Index</h2>
      ${comprehensiveReport.citations.map(category => `
        <div class="subsection">
          <div class="subsection-title">${category.category}</div>
          <table>
            <thead>
              <tr>
                <th>Title</th>
                <th>Citation</th>
                <th>Link</th>
              </tr>
            </thead>
            <tbody>
              ${category.citations.map(citation => `
                <tr>
                  <td>${citation.title}</td>
                  <td>${citation.cite}</td>
                  <td>
                    ${citation.link ? 
                      `<a href="${citation.link}" class="link" target="_blank">View Regulation</a>` 
                      : 'N/A'}
                  </td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      `).join('')}
    </div>

    <div class="footer">
      <p><strong>EHS & Insurance Readiness Assessment Tool</strong></p>
      <p>Report Generated: ${new Date().toLocaleString()}</p>
      <p>This report is for informational purposes only and does not constitute professional advice.</p>
      <p>Consult with qualified professionals for specific guidance.</p>
    </div>
  </div>
</body>
</html>
  `;

  // Create a Blob from the HTML content
  const blob = new Blob([htmlContent], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  
  // Create a temporary link and trigger download
  const link = document.createElement('a');
  link.href = url;
  link.download = `EHS-Risk-Assessment-Report-${new Date().toISOString().split('T')[0]}.html`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  // Clean up
  URL.revokeObjectURL(url);
  
  // Show success message
  alert('Report downloaded successfully!\n\nOpen the HTML file in your browser, then use Print (Ctrl+P) and "Save as PDF" to create a PDF version.');
};

// Helper function to generate insurance considerations for the PDF
const generateInsuranceForReport = (formData) => {
  // This is a simplified version - you can copy the full logic from getInsuranceConsiderations
  const considerations = [];

  const hasEnvironmentalRisk = formData.facilities.some(f =>
    f.airEmissions || f.waterDischarges || f.hazardousWaste || f.hazardousMaterialsStorage
  ) || formData.hazards.includes('Hazardous Chemicals');

  const isContractor = formData.operations.includes('Installation, Service, Maintenance, or Repair') ||
                       formData.industry === 'construction';
  const isConsulting = formData.industry === 'consulting';
  const hasProducts = formData.products.length > 0;

  // Essential provisions
  considerations.push({
    area: 'Essential Policy Provisions',
    insight: 'All insurance programs should include: (1) Blanket Additional Insured with Primary & Non-Contributory and Waiver of Subrogation. (2) Per Project Aggregate limits. (3) Notice of Cancellation provisions (30-60 days). (4) Defense costs outside limits.',
    coverage: 'Policy Enhancements'
  });

  if (!formData.hasPollutionCoverage && hasEnvironmentalRisk) {
    considerations.push({
      area: 'Missing Pollution Liability Coverage',
      insight: 'Your operations present environmental exposure, but pollution coverage is not in place. Standard CGL policies exclude pollution. Evaluate: Contractors Pollution Liability (CPL), Transportation Pollution, Non-Owned Disposal Site Liability (NODSL), Products Pollution, and Premises Pollution/Site Environmental Liability.',
      coverage: 'Pollution Liability Required'
    });
  }

  if (hasProducts) {
    considerations.push({
      area: 'Products Pollution Liability',
      insight: 'Given your product operations, consider both Products Pollution Liability and Site/Premises Pollution coverage to address product-related and operational environmental risks.',
      coverage: 'Products Pollution + Site'
    });
  }

  if (isContractor) {
    considerations.push({
      area: 'Contractors Pollution Liability',
      insight: 'Contracting operations at third-party locations create pollution exposure excluded under standard CGL. Contractors Pollution Liability (CPL) is essential and often contractually required.',
      coverage: 'CPL Required'
    });
  }

  if (isConsulting) {
    considerations.push({
      area: 'Professional Liability (E&O)',
      insight: 'Consulting, testing, and monitoring services require Professional Liability coverage for claims from professional negligence. This is distinct from pollution liability.',
      coverage: 'E&O Required'
    });
  }

  considerations.push({
    area: 'Annual Market Review',
    insight: 'Insurance markets are dynamic and competitive. Organizations should conduct annual market reviews to ensure optimal pricing and coverage terms. Carrier appetite and pricing can vary significantly year-to-year.',
    coverage: 'Best Practice'
  });

  considerations.push({
    area: 'Captive Insurance Consideration',
    insight: 'Organizations with $500K+ annual premium, consistent loss history, or difficult-to-place risks may benefit from captive structures. Captives require regulatory compliance but can provide long-term cost savings. This is not a requirement but worth evaluating.',
    coverage: 'Optional Structure'
  });

  return considerations;
};